import { MapPin, Phone, User, Building, ShoppingBag, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/modules/shared/components/ui/card'
import { Badge } from '@/modules/shared/components/ui/badge'
import { cn } from '@/modules/shared/lib/utils'
import { type RouteClient } from '../hooks/use-route-clients.hook'

interface RouteClientCardProps {
	entity: RouteClient
	clientSelected: RouteClient | null
	onSelectEntity: (entity: RouteClient) => void
}

export function RouteClientCardComponent({
	entity,
	clientSelected,
	onSelectEntity
}: RouteClientCardProps) {
	const formatCnpjCpf = (cnpjCpf: string) => {
		if (!cnpjCpf) return 'CPF/CNPJ não informado'

		// Formata CPF (11 dígitos)
		if (cnpjCpf.length <= 11) {
			return cnpjCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
		}

		// Formata CNPJ (14 dígitos)
		return cnpjCpf.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
	}

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat('pt-BR', {
			style: 'currency',
			currency: 'BRL'
		}).format(value)
	}

	const isSelected = clientSelected?.idEntidade === entity.idEntidade;

	return (
		<Card
			className={cn(
				'cursor-pointer transition-all duration-200 ease-in-out hover:shadow-md',
				isSelected
					? 'bg-sky-500/30'
					: 'hover:bg-gray-50'
			)}
			onClick={() => onSelectEntity(entity)}
		>
			<CardHeader className="p-3 pb-0">
				<CardTitle className="text-sm flex justify-between items-start">
					<span className="text-xs line-clamp-3">({entity.idEntidade}) {entity.razaoSocialNome} </span>
					{entity.pedidoRealizado && (
						<Badge
							variant="outline"
							className="ml-1 px-1 py-0 h-5 bg-green-50 text-green-700 border-green-300 flex items-center"
						>
							<CheckCircle className="h-3 w-3 mr-1" />
							<span className="text-[10px] text-nowrap">Realizado</span>
						</Badge>
					)}
				</CardTitle>
			</CardHeader>

			<CardContent className="p-3 pt-2 text-xs space-y-1">
				<div className="flex items-start">
					<Building className="h-3.5 w-3.5 mr-1.5 text-gray-500 mt-0.5" />
					<span className="line-clamp-2">{formatCnpjCpf(entity.cnpjCpf)}</span>
				</div>

				{entity.logradouro && (
					<div className="flex items-start">
						<MapPin className="h-3.5 w-3.5 mr-1.5 text-gray-500 mt-0.5" />
						<span className="line-clamp-3 text-xs">
							{entity.logradouro}, {entity.numero}
							{entity.bairro && `, ${entity.bairro}`}
						</span>
					</div>
				)}

				{entity.cidade && (
					<div className="flex items-start">
						<MapPin className="h-3.5 w-3.5 mr-1.5 text-gray-500 opacity-0" />
						<span className="line-clamp-1 text-xs">{entity.cidade} - {entity.uf}</span>
					</div>
				)}

				{entity.fone && (
					<div className="flex items-start">
						<Phone className="h-3.5 w-3.5 mr-1.5 text-gray-500 mt-0.5" />
						<span className="line-clamp-1">{entity.fone}</span>
					</div>
				)}

			</CardContent>
		</Card>
	)
}